// pages/Following — TRIL-504: a list of the people the caller follows. Clicking a row opens that
// user's profile (which shows all their posts). Replaces the old FOLLOWING posts-feed, which was a
// working but empty feed that read as broken and didn't match the "who do I follow" expectation.

import { useQuery } from '@apollo/client';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import AvatarComponent from '../../components/UI/Avatar/Avatar.component';
import { PRIVATE_URIS } from '../../constants/privateURIs.constants';
import { useAuth } from '../../context/AuthContext';
import { GET_FOLLOWING } from '../../graphql/resolvers/followQueries/getFollowing.gql';
import { resolveNavRoute } from '../../helpers/resolveNavRoute';
import styles from './following.module.scss';

const FollowingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(GET_FOLLOWING, {
    variables: { userId: user?.id ?? '' },
    skip: !user?.id,
    fetchPolicy: 'cache-and-network',
  });

  const following = data?.getFollowing ?? [];

  return (
    <div className={styles.page}>
      <h1 className={styles.heading}>Following</h1>

      {loading && following.length === 0 ? (
        <p className={styles.state}>Loading…</p>
      ) : error ? (
        <p className={styles.state}>Couldn't load who you follow.</p>
      ) : following.length === 0 ? (
        <p className={styles.state}>You're not following anyone yet.</p>
      ) : (
        <div className={styles.list}>
          {following.map((u) => {
            const username = u.username ?? 'unknown';
            return (
              <button
                type="button"
                key={u.id}
                className={styles.row}
                // Deleted users are dropped server-side; guard the rare null-username row so it isn't
                // a clickable no-op (nowhere to navigate without a username).
                disabled={!u.username}
                onClick={() =>
                  u.username &&
                  navigate(resolveNavRoute(PRIVATE_URIS.PROFILE, u.username))
                }
                aria-label={`View @${username}'s profile`}
              >
                <span className={styles.avatar}>
                  <AvatarComponent
                    reloadAvatar={false}
                    userAvatarUrl={u.avatar ?? undefined}
                    username={username}
                    firstName={u.displayName ?? username}
                  />
                </span>
                <span className={styles.meta}>
                  <span className={styles.username}>@{username}</span>
                  {u.displayName && u.displayName !== username ? (
                    <span className={styles.displayName}>{u.displayName}</span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FollowingPage;
